<template>
    <Layout>
        <section class="relative overflow-hidden">
            <div class="account-menu-mobile">
                <h2 v-if="isB2B">My Account</h2>
                <h2 v-else>Hi, {{ user.username }}!</h2>
                <div class="user-info" v-if="isB2B">
                    <p>{{ user.email }}</p>
                    <p>Acct. #{{ user.dealer_no }}</p>
                </div>
            </div>
            <div id="account-menu-mobile-items" class="nav-selector bg-rothcoCream">
                <details ref="details"  class="nav-selector__details">
                    <summary class="nav-selector__title">
                        Account Menu
                    </summary>
                    <div class="nav-selector__content">
                        <ul class="navlinks">
                            <li v-for="(link, index) in accountLinks" :key="link.name" :id="`mobile_account_link_${index}`">
                                <Link  :onStart="closeDetails" :href="link.alias ? getRoute(link) : 'index/' + link.link" :aria-label="link.ariaLabel" :class="{ current: activeLink === link }">{{ link.name }}</Link>
                            </li>
                            <li><a :href="route('web.logout')" class="logout" aria-label="Log Out"><AccountRedIcon alt="account icon" /> Log Out</a></li>
                        </ul>
                    </div>
                </details>
            </div>
            <div class="account-page-container">
                <aside class="aside-menu-desktop bg-rothcoCream">
                    <div class="aside-menu-content">
                        <div class="account-menu-desktop">
                            <h2 v-if="isB2B">My Account</h2>
                            <h2 v-else>Hi, {{ user.username }}!</h2>
                            <div class="user-info" v-if="isB2B">
                                <p>{{ user.email }}</p>
                                <p>Acct. #{{ user.dealer_no }}</p>
                            </div>
                            <div class="navlinks">
                                <Link :class="{
                                    current: activeLink === link
                                }" v-for="(link, index) in accountLinks" :key="link.name" :href="link.alias ? getRoute(link): 'index/' + link.link" :id="`account_link_${index}`" :aria-label="link.ariaLabel">{{ link.name }}</Link>
                                <a :href="route('web.logout')" class="logout"><AccountRedIcon  alt="account icon" /> Log Out</a>
                            </div>
                        </div>
                    </div>
                </aside>
                <div class="account-dashboard" style="overflow-x:scroll">
                    <slot></slot>
                </div>
            </div>
        </section>
    </Layout>
</template>
<script lang="ts" setup>
    import Layout from "@/Shared/Layout.vue";
    import { Link } from "@inertiajs/vue3";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { computed, inject, onMounted, ref } from "vue";
    import { onClickOutside } from "@vueuse/core";
    import AccountRedIcon from "@icons/account-icon-red.svg";
    let route = inject("route");
    let store = useSettingsStore();
    let { user, canSeePricing, canPlaceOrders, canHandleAdmin } = storeToRefs(store);
    let isB2B = computed(() => user.value.is_dealer);

    let accountLinks = computed(() => {
        if (isB2B.value) {
            return [
                {
                    name: "Dashboard",
                    link: "/my-account",
                    alias: "account.show",
                    ariaLabel: "Dashboard",
                    visible: true,
                },
                {
                    name: "Credit Cards",
                    ariaLabel: "Credit Cards",
                    link: "/my-account/payment-methods",
                    alias: "account.paymentMethods",
                    visible: canPlaceOrders.value
                },
                {
                    name: "Company Info",
                    link: "/my-account/company-info",
                    alias: "account.companyInfo",
                    ariaLabel: "Company Info",
                    visible: canHandleAdmin.value
                },
                {
                    name: "Order History",
                    link: "/my-account/orders",
                    alias: "account.orders",
                    ariaLabel: "Order History",
                    visible: canSeePricing.value
                },
                {
                    name: "Payment History",
                    link: "/my-account/payments",
                    alias: "account.paymentHistory",
                    ariaLabel: "Payment History",
                    visible: canPlaceOrders.value
                },
                {
                    name: "Favorites",
                    link: "/my-account/favorites",
                    alias: "account.favorites",
                    ariaLabel: "Favorites",
                    visible: true
                },
                // there is no route for saved lists on production??
                {
                    name: "Saved Lists",
                    link: "/my-account/lists",
                    alias: "account.lists",
                    ariaLabel: "Saved Lists",
                    visible: true
                },
                {
                    name: "User Accounts",
                    link: "/my-account/accounts",
                    alias: "account.accounts",
                    ariaLabel: "User Accounts",
                    visible: canHandleAdmin.value
                },
                {
                    name: "Password & Security",
                    link: "/my-account/password-security",
                    alias: "account.passwordSecurity",
                    ariaLabel: "Password & Security",
                    visible: true
                },
                {
                    name: "API Portal Settings",
                    alias: "account.apiPortal",
                    ariaLabel: "API Portal Settings",
                    visible: true
                },
                {
                    name: "API Documentation",
                    alias: "api.docs",
                    params: ["about-the-api"],
                    ariaLabel: "API Documentation",
                    visible: true
                }
            ].filter(link => link.visible);
        } else {
            return [
                {
                    name: "Dashboard",
                    link: "/my-account",
                    alias: "account.show",
                    ariaLabel: "Dashboard"
                },
                {
                    name: "Order History",
                    link: "/my-account/orders",
                    alias: "account.orders",
                    ariaLabel: "Order History"
                },
                {
                    name: "Profile",
                    link: "/my-account/profile",
                    alias: "account.profile",
                    ariaLabel: "Profile"
                },
                {
                    name: "Password & Security",
                    link: "/my-account/password-security",
                    alias: "account.passwordSecurity",
                    ariaLabel: "Password & Security"
                },
                {
                    name: "Address Book",
                    link: "/my-account/addresses",
                    alias: "account.addresses",
                    ariaLabel: "Address Book"
                },
                {
                    name: "Payment Methods",
                    link: "/my-account/payment-methods",
                    alias: "account.paymentMethods",
                    ariaLabel: "Payment Methods"
                },
                {
                    name: "Favorites",
                    link: "/my-account/favorites",
                    alias: "account.favorites",
                    ariaLabel: "Favorites"
                }
            ];
        }
    });

    let activeLink = ref(null)
    onMounted(() => {
        let url = window.location.href;
        let a_link = accountLinks.value.find((link) => {
            if(link.params) {
                return url == route(link.alias, link.params);
            }
            return url == route(link.alias);
        });

        activeLink.value = a_link

    });


    let details = ref();

    function closeDetails() {
        details.value.removeAttribute("open");
    }

    onClickOutside(details, closeDetails);

    function getRoute(link) {
        if (link.params) {
            return route(link.alias, link.params);
        }
        return route(link.alias);
    }

</script>
